import { h, Fragment } from 'preact'
import { useSignal } from '@preact/signals'
import { useEffect, useRef } from 'preact/hooks'
import { DirectUpload } from '@rails/activestorage'
import { rails_direct_uploads_path } from '../../../routes'
import { MemberPicker } from './MemberPicker.jsx'
import { ModalShell } from './ModalShell.jsx'
import { chatApi } from '../chat-api.js'
import { t } from '../chat-i18n.js'
import { RequiredAsterisk } from '../../../components/shared/RequiredAsterisk.jsx'

/**
 * EditGroupModal — load room via chatApi.rooms.show, edit name/members/avatar.
 *
 * Props: { open, roomId, onClose, onUpdated(room), currentPersonId }
 */
export function EditGroupModal({ open, roomId, onClose, onUpdated, currentPersonId }) {
  const loading        = useSignal(true)
  const name           = useSignal('')
  const currentMembers = useSignal([]) // Person[] excluding self (display)
  const removeIds      = useSignal([]) // Number[] marked for removal
  const addMembers     = useSignal([]) // Person[] to add
  const avatarUrl      = useSignal(null)
  const avatarPreview  = useSignal(null)
  const avatarSignedId = useSignal(null)
  const uploading      = useSignal(false)
  const saving         = useSignal(false)
  const error          = useSignal(null)
  const fileInputRef   = useRef(null)

  useEffect(() => {
    if (!open || !roomId) return
    loading.value = true
    error.value = null
    removeIds.value = []
    addMembers.value = []
    avatarSignedId.value = null
    if (avatarPreview.value) {
      URL.revokeObjectURL(avatarPreview.value)
      avatarPreview.value = null
    }

    chatApi.rooms.show(roomId).then(async res => {
      if (!res.ok) {
        error.value = t('chat.rooms.not_found')
        return
      }
      const room = await res.json
      name.value = room.name || room.display_name || ''
      avatarUrl.value = room.avatar_url || null
      const members = (room.members || []).filter(p => Number(p.id) !== Number(currentPersonId))
      currentMembers.value = members
    }).catch(() => {
      error.value = t('chat.rooms.not_found')
    }).finally(() => { loading.value = false })
  }, [open, roomId]) // eslint-disable-line

  function handleClose() {
    if (avatarPreview.value) URL.revokeObjectURL(avatarPreview.value)
    avatarPreview.value = null
    onClose?.()
  }

  function toggleRemove(personId) {
    const id = Number(personId)
    if (removeIds.value.includes(id)) {
      removeIds.value = removeIds.value.filter(x => x !== id)
    } else {
      removeIds.value = [...removeIds.value, id]
    }
  }

  function onAvatarChange(e) {
    const file = e.target.files?.[0]
    if (!file) return

    if (avatarPreview.value) URL.revokeObjectURL(avatarPreview.value)
    avatarPreview.value = URL.createObjectURL(file)
    avatarSignedId.value = null
    uploading.value = true

    const upload = new DirectUpload(file, rails_direct_uploads_path())
    upload.create((err, blob) => {
      uploading.value = false
      if (err) {
        error.value = String(err)
        return
      }
      avatarSignedId.value = blob.signed_id
    })
  }

  async function handleSubmit(e) {
    e?.preventDefault()
    const groupName = name.value.trim()
    if (!groupName || saving.value || uploading.value || !roomId) return

    saving.value = true
    error.value = null
    try {
      const params = {
        name: groupName,
        add_person_ids: addMembers.value.map(p => p.id),
        remove_person_ids: removeIds.value,
      }
      if (avatarSignedId.value) params.avatar_signed_id = avatarSignedId.value

      const res = await chatApi.rooms.update(roomId, params)
      if (!res.ok) {
        const body = await res.json.catch(() => ({}))
        error.value = body.error || t('chat.rooms.not_authorized')
        return
      }
      const room = await res.json
      onUpdated?.(room)
      handleClose()
    } catch {
      error.value = t('chat.rooms.not_authorized')
    } finally {
      saving.value = false
    }
  }

  const excludeIds = [
    Number(currentPersonId),
    ...currentMembers.value.map(p => Number(p.id)),
  ]
  const previewSrc = avatarPreview.value || avatarUrl.value
  const canSubmit = name.value.trim().length > 0 && !saving.value && !uploading.value && !loading.value

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      title={t('chat.rooms.edit_group_title')}
      iconClass="bi-pencil"
      footer={
        <>
          <button type="button" class="btn btn-ghost-secondary" onClick={handleClose} disabled={saving.value}>
            {t('chat.rooms.cancel')}
          </button>
          <button type="button" class="btn btn-primary" onClick={handleSubmit} disabled={!canSubmit}>
            {saving.value
              ? <span class="spinner-border spinner-border-sm" role="status" />
              : t('chat.rooms.save_changes')}
          </button>
        </>
      }
    >
      {loading.value ? (
        <div class="text-center py-4">
          <span class="spinner-border text-secondary" role="status" />
        </div>
      ) : (
        <>
          {error.value && <div class="alert alert-danger py-2 small">{error.value}</div>}

          <div class="mb-4 d-flex flex-column align-items-center gap-2">
            <div class="chat-group-avatar-preview">
              {previewSrc
                ? <img src={previewSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                : <i class="bi bi-people-fill" />}
            </div>
            <label class="btn btn-sm btn-outline-secondary" style={{ cursor: 'pointer' }}>
              <i class="bi bi-camera me-1" />
              {t('chat.rooms.change_avatar')}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                class="d-none"
                onChange={onAvatarChange}
                disabled={saving.value}
              />
            </label>
          </div>

          <div class="mb-4">
            <label class="form-label fw-medium">
              {t('chat.rooms.group_name_label')}
              <RequiredAsterisk />
            </label>
            <input
              type="text"
              class="form-control"
              placeholder={t('chat.rooms.group_name_placeholder')}
              value={name.value}
              onInput={e => { name.value = e.target.value }}
              required
              disabled={saving.value}
            />
          </div>

          {currentMembers.value.length > 0 && (
            <div class="mb-3">
              <label class="form-label fw-medium">{t('chat.rooms.current_members_label')}</label>
              <div class="chat-edit-members-list">
                {currentMembers.value.map(person => {
                  const marked = removeIds.value.includes(Number(person.id))
                  return (
                    <div
                      key={person.id}
                      class={`chat-edit-member-item ${marked ? 'chat-edit-member-item--removing' : ''}`}
                    >
                      <div class="d-flex align-items-center gap-2 flex-grow-1 overflow-hidden">
                        {person.avatar_url
                          ? <img src={person.avatar_url} class="chat-room-avatar flex-shrink-0" width={28} height={28} alt="" />
                          : (
                            <div class="chat-room-avatar chat-room-avatar--initials chat-room-avatar--direct flex-shrink-0"
                                 style={{ width: 28, height: 28, fontSize: '0.7rem' }}>
                              {(person.name || '?').slice(0, 1).toUpperCase()}
                            </div>
                          )}
                        <span class={`small text-truncate ${marked ? 'text-decoration-line-through text-muted' : ''}`}>
                          {person.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        class="chat-edit-member-remove btn btn-link p-0 border-0"
                        title={t('chat.rooms.remove_member')}
                        onClick={() => toggleRemove(person.id)}
                        disabled={saving.value}
                      >
                        <i class={`bi bi-x-circle text-danger ${marked ? '' : 'opacity-50'}`} />
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div class="mb-1">
            <label class="form-label fw-medium">{t('chat.rooms.add_members_label')}</label>
            <MemberPicker
              selected={addMembers.value}
              onChange={persons => { addMembers.value = persons }}
              excludeIds={excludeIds}
              multiple
              disabled={saving.value}
            />
          </div>
        </>
      )}
    </ModalShell>
  )
}
